import React, { useState, useEffect } from 'react';
import { getCiudadById, createCiudad, updateCiudad } from '../services/ciudadService';
import { getDepartamentos } from '../services/departamentoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, MapPin, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function CiudadForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        departamento: null
    });

    const [departamentos, setDepartamentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);

    useEffect(() => {
        cargarDepartamentos();
        if (isEditMode) {
            cargarCiudad();
        }
    }, [id]);

    const cargarDepartamentos = async () => {
        try {
            const data = await getDepartamentos();
            setDepartamentos(data);
        } catch (error) {
            toast.error('Error al cargar departamentos');
        }
    };

    const cargarCiudad = async () => {
        try {
            const data = await getCiudadById(id);
            setFormData(data);
        } catch (error) {
            toast.error('Error al cargar ciudad');
            navigate('/parametrizacion/ciudades');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!formData.departamento?.id) {
                toast.error('Debe seleccionar un departamento');
                return;
            }

            setLoading(true);
            if (isEditMode) {
                await updateCiudad(id, formData);
                toast.success('Ciudad actualizada');
            } else {
                await createCiudad(formData);
                toast.success('Ciudad creada con éxito');
            }
            setTimeout(() => navigate('/parametrizacion/ciudades'), 1000);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <div className="p-20 text-center uppercase font-black text-slate-400 tracking-[0.2em]">Cargando...</div>;
    }

    return (
        <div className="max-w-2xl mx-auto bg-white shadow-xl border border-slate-200 mb-10 overflow-hidden font-sans">
            <Toaster position="top-right" />
            <div className="bg-slate-700 p-5 flex items-center justify-between text-white">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/parametrizacion/ciudades')} className="p-2 hover:bg-slate-600 rounded-full transition-all" type="button">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-bold uppercase tracking-tight">
                        {isEditMode ? 'Editar Ciudad' : 'Nueva Ciudad'}
                    </h1>
                </div>
                <MapPin className="w-10 h-10 text-emerald-500 opacity-30" />
            </div>

            <form onSubmit={handleSubmit} className="p-10 space-y-8">
                <div className="space-y-6">
                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Nombre de la Ciudad</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase text-slate-800 focus:border-emerald-500 outline-none"
                            value={formData.nombre}
                            onChange={(e) => setFormData({ ...formData, nombre: e.target.value.toUpperCase() })}
                        />
                    </div>

                    <div>
                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Departamento</label>
                        <select
                            required
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-sm font-bold uppercase focus:border-emerald-500 outline-none"
                            value={formData.departamento?.id || ''}
                            onChange={(e) => setFormData({ ...formData, departamento: { id: e.target.value } })}
                        >
                            <option value="">Seleccione Departamento</option>
                            {departamentos.map(d => (
                                <option key={d.id} value={d.id}>{d.nombre}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="pt-8 border-t flex justify-end gap-4">
                    <button type="button" onClick={() => navigate('/parametrizacion/ciudades')} className="text-xs font-black uppercase text-slate-400 hover:text-slate-600">DESCARTAR</button>
                    <button type="submit" disabled={loading} className="px-10 py-3 bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-500" />}
                        {isEditMode ? 'ACTUALIZAR' : 'GUARDAR REGISTRO'}
                    </button>
                </div>
            </form>
        </div>
    );
}
